import React, { Fragment, useCallback } from "react";

import { Button, Col, Row, Table, TableProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useNavigate, createSearchParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { ActionButton, InputString } from "../editor/components";
import { Step, StepPresentation } from "../lib/schema.generated";
import { useVapianoConfigContext } from "./vapianoConfigContext";
import { ITEMS_PER_PAGE, emptyTranslatedString } from "../lib/settings";
import { useEditorContext } from "../editor/context";
import { TranslatedString } from "../lib/scalars";
import { useQueryParam } from "use-query-params";
import { OwnStringParam, OwnNumberParam, SortParam } from "../utils";
import { SortOrder } from "antd/es/table/interface";

export const Steps: React.FC = () => {
  const ctx = useVapianoConfigContext();
  const ctxEditor = useEditorContext();
  const navigate = useNavigate();
  //const [searchValue, setSearchValue] = React.useState<string>("");
  const [page, setPage] = useQueryParam("page", OwnNumberParam);
  const [searchValue, setSearchValue] = useQueryParam(
    "keyword",
    OwnStringParam
  );
  const [sort, setSort] = useQueryParam("order", SortParam);

  const columns: ColumnsType<Step> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: (a: Step, b: Step) => a.id - b.id,
      sortOrder:
        sort !== undefined && sort.columnKey === "id"
          ? (sort.order as SortOrder)
          : undefined,
    },
    /*{
      title: "Index",
      dataIndex: "id",
      key: "id",
      render: (v: number) => {
        return ctx.document.menu.steps.findIndex((step) => step.id === v);
      },
      sorter: (a: Step, b: Step) => {
        const indexA = ctx.document.menu.steps.findIndex(
          (step) => step.id === a.id
        );
        const indexB = ctx.document.menu.steps.findIndex(
          (step) => step.id === b.id
        );

        return indexA - indexB;
      },
    },*/
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: TranslatedString) => name[ctx.useLanguageKey],
      sorter: (a: Step, b: Step) =>
        a.name[ctx.useLanguageKey].localeCompare(b.name[ctx.useLanguageKey]),
      sortOrder:
        sort !== undefined && sort.columnKey === "name"
          ? (sort.order as SortOrder)
          : undefined,
    },
    {
      title: "EditorName",
      dataIndex: "editorName",
      key: "editorName",
      sorter: (a: Step, b: Step) => a.editorName.localeCompare(b.editorName),
      sortOrder:
        sort !== undefined && sort.columnKey === "editorName"
          ? (sort.order as SortOrder)
          : undefined,
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "id",
      render: (_id: number, data: Step) => {
        const stepIndex = ctx.document.menu.steps.findIndex(
          (step) => step.id === data.id
        );
        return (
          <ActionButton
            label="Remove"
            action="remove"
            type="dashed"
            dataPath={["menu", "steps"]}
            value={ctx.document.menu.steps}
            defaultValue={stepIndex}
          />
        );
      },
    },
  ];

  const getQueryParams = useCallback((): string => {
    const params: Record<string, string> = {};
    page !== null && page !== undefined
      ? (params["page"] = page.toString())
      : null;
    searchValue !== "" ? (params["keyword"] = searchValue) : null;
    sort !== undefined
      ? (params["order"] = `${sort.columnKey}_${sort.order.substring(0, 3)}`)
      : null;
    return createSearchParams(params).toString();
  }, [page, searchValue, sort]);

  const onRow = useCallback(
    (step: Step) => {
      return {
        onClick: () => {
          navigate(`${step.id}`, {
            state: {
              queryParams: getQueryParams(),
            },
          });
        },
      };
    },
    [navigate, getQueryParams]
  );

  const onSearchValueChange = useCallback(
    (value: string | null) => {
      if (value !== null) {
        setSearchValue(value);
      } else {
        setSearchValue("");
      }
    },
    [setSearchValue]
  );

  const ids = ctx.document.menu.steps.map((step) => step.id);
  const maxId = Math.max(...ids);

  const onAddNewStepClick = useCallback(() => {
    const newStep: Step = {
      id: maxId === Infinity || maxId === -Infinity ? 1 : maxId + 1,
      name: emptyTranslatedString,
      editorName: "",
      description: null,
      selectionText: null,
      presentation: StepPresentation.List,
      stepOptions: [],
      maxDistinct: null,
      maxTotal: null,
      minDistinct: null,
      minTotal: null,
      singleSelect: null,
      columnCount: 0,
      //isExtra: false,
    };
    ctxEditor.dispatch({
      type: "addItem",
      path: ["menu", "steps"],
      item: newStep,
    });
    navigate(`${newStep.id}`, {
      state: {
        queryParams: getQueryParams(),
      },
    });
  }, [ctxEditor, maxId, navigate, getQueryParams]);

  const onTableChange: TableProps<Step>["onChange"] = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    pagination,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    filters,
    sorter,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    extra
  ) => {
    setSort(sorter as { order: string; columnKey: string });
  };

  return (
    <Fragment>
      <PageHeader
        title="Steps"
        extra={
          <Button type="primary" onClick={onAddNewStepClick}>
            Add New Step
          </Button>
        }
      />
      <Row style={{ justifyContent: "center", marginBottom: 20 }}>
        <Col span={8}>
          <InputString
            placeholder="Search in name"
            value={searchValue}
            onChange={onSearchValueChange}
          />
        </Col>
      </Row>

      <Table
        onRow={onRow}
        rowKey="id"
        columns={columns}
        tableLayout="auto"
        sticky={true}
        // eslint-disable-next-line react/jsx-no-bind
        onChange={onTableChange}
        showSorterTooltip={false}
        pagination={{
          pageSize: ITEMS_PER_PAGE,
          position: ["topLeft"],
          hideOnSinglePage: true,
          showSizeChanger: false,
          current: page !== null && page !== undefined ? page : 1,
          onChange: (page) => {
            setPage(page);
          },
        }}
        dataSource={ctx.document.menu.steps.filter((step) => {
          return (
            step.name["hu"]
              .toLocaleLowerCase()
              .includes(searchValue.toLocaleLowerCase()) ||
            step.name["en"]
              .toLocaleLowerCase()
              .includes(searchValue.toLocaleLowerCase()) ||
            step.editorName
              .toLocaleLowerCase()
              .includes(searchValue.toLocaleLowerCase()) ||
            step.id === Number(searchValue)
          );
        })}
      />
    </Fragment>
  );
};
